import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as LucideIcons from "lucide-react";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Users,
  Settings,
  CheckCircle2,
  MapPin,
  Briefcase,
  Home,
  Clock,
  Sparkles,
  X,
  User,
  Mail,
  Smartphone,
  Linkedin,
  GitFork,
  Upload,
  BarChart2,
  Building2,
  UserCheck,
  Code2,
  ChevronDown,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ROLE_DETAILS } from "../data/openPositionsData";

const getRoleIcon = (name) => {
  return LucideIcons[name] || Sparkles;
};

const AiEngineerRole = ({ defaultRoleId }) => {
  const { roleId } = useParams();
  const currentRoleId = roleId || defaultRoleId || "ai-engineer";
  const role = ROLE_DETAILS[currentRoleId] || ROLE_DETAILS["ai-engineer"];

  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    linkedin: "",
    portfolio: "",
    employmentStatus: "",
    experience: "",
    company: "",
    currentRole: "",
    noticePeriod: "",
    currentCtc: "",
    expectedCtc: "",
    keySkills: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentRoleId]);

  const openModal = () => {
    setIsApplyModalOpen(true);
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size exceeds 5 MB limit.");
        return;
      }
      setResumeFile(file);
      toast.success(`Resume attached: ${file.name}`);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName.trim()) {
      toast.error("Please enter your full name.");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    if (!formData.phone.trim()) {
      toast.error("Please enter your phone number.");
      return;
    }
    if (!/^\d{10}$/.test(formData.phone.trim())) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }
    if (!formData.city) {
      toast.error("Please select your current city.");
      return;
    }
    if (!resumeFile) {
      toast.error("Please upload your resume / CV.");
      return;
    }
    if (!formData.employmentStatus) {
      toast.error("Please select your employment status.");
      return;
    }
    if (!formData.experience) {
      toast.error("Please select your years of experience.");
      return;
    }
    if (!formData.noticePeriod) {
      toast.error("Please select your notice period.");
      return;
    }
    if (!formData.expectedCtc.trim()) {
      toast.error("Please enter your expected CTC.");
      return;
    }
    const cleanExpected = formData.expectedCtc.replace(/,/g, "").trim();
    if (!/^\d+$/.test(cleanExpected) || Number(cleanExpected) <= 0) {
      toast.error("Please enter a valid numeric expected CTC (e.g. 18,00,000).");
      return;
    }
    if (formData.currentCtc.trim()) {
      const cleanCurrent = formData.currentCtc.replace(/,/g, "").trim();
      if (!/^\d+$/.test(cleanCurrent)) {
        toast.error("Please enter a valid numeric current CTC.");
        return;
      }
    }
    if (!formData.keySkills.trim()) {
      toast.error("Please enter your key skills.");
      return;
    }

    toast.success(
      `Application submitted successfully for ${role.title}! We'll review your profile and get in touch shortly.`
    );
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      city: "",
      linkedin: "",
      portfolio: "",
      employmentStatus: "",
      experience: "",
      company: "",
      currentRole: "",
      noticePeriod: "",
      currentCtc: "",
      expectedCtc: "",
      keySkills: "",
    });
    setResumeFile(null);
    setIsApplyModalOpen(false);
  };

  return (
    <div className="min-h-screen w-full bg-white">
      <Navbar />

      <main>
        {/* Hero Section - exact match to user screenshot */}
        <section
          className="relative w-full overflow-hidden mt-[76px] sm:mt-[80px]"
          style={{ backgroundColor: "rgba(1, 7, 16, 1)", minHeight: "560px" }}
        >
          {/* Panoramic AI Engineer Photo on Right - Full Bleed exactly matching screenshot */}
          <div className="absolute inset-y-0 right-0 h-full w-full lg:w-[56%] xl:w-[54%] 2xl:w-[52%] overflow-hidden pointer-events-none">
            <img
              src={role.heroImage || "/images/careers/ai-engineer-hero.png"}
              alt={role.title}
              className="h-full w-full object-cover object-[center_top]"
            />
            {/* Linear Gradient matching screenshot - fades smoothly into rgba(1, 7, 16, 1) on left */}
            <div
              className="absolute inset-0 hidden lg:block"
              style={{
                background:
                  "linear-gradient(90deg, rgba(1, 7, 16, 1) 0%, rgba(1, 7, 16, 0.95) 14%, rgba(1, 7, 16, 0.42) 38%, rgba(1, 7, 16, 0) 65%)",
              }}
            />
            {/* Top gradient overlay for seamless navbar blending */}
            <div
              className="absolute inset-x-0 top-0 h-24 pointer-events-none hidden lg:block"
              style={{
                background:
                  "linear-gradient(180deg, rgba(1, 7, 16, 0.85) 0%, rgba(1, 7, 16, 0) 100%)",
              }}
            />
            {/* Mobile / Tablet dark overlay */}
            <div
              className="absolute inset-0 lg:hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(1, 7, 16, 0.95) 0%, rgba(1, 7, 16, 0.78) 50%, rgba(1, 7, 16, 0.95) 100%)",
              }}
            />
          </div>

          {/* Ambient Blue Background Glow on Left */}
          <div
            className="pointer-events-none absolute"
            style={{
              width: 750,
              height: 440,
              left: -80,
              top: 40,
              background: "radial-gradient(circle, #173862 0%, rgba(1, 7, 16, 0) 70%)",
              filter: "blur(90px)",
              opacity: 0.65,
            }}
          />

          {/* Left Text Content & Actions */}
          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px] pt-12 sm:pt-14 lg:pt-16 pb-16 sm:pb-20 lg:pb-24">
            <div className="flex max-w-[540px] flex-col justify-center">
              {/* Back to all roles link */}
              <Link
                to="/careers/open-positions"
                className="group inline-flex items-center gap-2 text-[13.5px] font-medium text-[#94A3B8] hover:text-[#00D2EE] transition-colors mb-6 w-fit"
              >
                <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                Back to all roles
              </Link>

              {/* Main Role Title */}
              <h1 className="text-[44px] sm:text-[54px] lg:text-[60px] font-bold text-white tracking-tight leading-[1.08]">
                {role.title}
              </h1>

              {/* Department | Location subtitle */}
              <div className="mt-3.5 flex items-center gap-2.5 text-[14px] sm:text-[14.5px]">
                <span className="font-semibold text-[#00D2EE]">
                  {role.dept}
                </span>
                <span className="text-[#475569]">|</span>
                <span className="text-[#CBD5E1]">{role.loc}</span>
              </div>

              {/* Catchphrase */}
              <h2 className="mt-7 text-[22px] sm:text-[25px] lg:text-[26px] font-medium text-white leading-[1.28] max-w-[510px]">
                {role.catchphrase}
              </h2>

              {/* Description */}
              <p className="mt-4 text-[15px] sm:text-[16px] leading-[26px] text-[#94A3B8] max-w-[480px]">
                {role.desc}
              </p>

              {/* CTA Button */}
              <div className="mt-8">
                <button
                  onClick={() => openModal()}
                  className="inline-flex items-center gap-2.5 rounded-full px-8 py-3.5 text-[15px] font-semibold text-white transition-all duration-300 hover:scale-[1.03] active:scale-95 shadow-[0_4px_24px_rgba(0,210,238,0.45)] cursor-pointer"
                  style={{
                    background: "linear-gradient(90deg, #00D2EE 0%, #2B7FFF 100%)",
                  }}
                >
                  Apply for this role <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Details Section - exact background rgba(245, 250, 254, 1) */}
        <section
          className="w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
              {/* Left Column: 3 Content Cards (8 cols) matching exact 3rd screenshot specs */}
              <div className="flex flex-col gap-6 lg:col-span-8">
                {/* Card 1: What you'll do */}
                <div
                  className="flex flex-col rounded-[16px] border bg-white p-7 sm:p-8"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    borderRadius: "16px",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#EBF8FE] text-[#0284C7] shadow-sm">
                      <FileText className="h-5 w-5" strokeWidth={2.2} />
                    </div>
                    <div>
                      <h3 className="text-[20px] font-bold text-[#0F172A] leading-snug">
                        What you'll do
                      </h3>
                      <p className="mt-1 text-[13.5px] text-[#64748B]">
                        Work on meaningful problems, from idea to impact.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col gap-3.5 pl-1 sm:pl-2">
                    {role.whatYoullDo.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-[#0284C7] mt-0.5" strokeWidth={2.2} />
                        <span className="text-[14px] leading-relaxed text-[#334155] font-medium">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card 2: What we're looking for */}
                <div
                  className="flex flex-col rounded-[16px] border bg-white p-7 sm:p-8"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    borderRadius: "16px",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#EBF8FE] text-[#0284C7] shadow-sm">
                      <Users className="h-5 w-5" strokeWidth={2.2} />
                    </div>
                    <div>
                      <h3 className="text-[20px] font-bold text-[#0F172A] leading-snug">
                        What we're looking for
                      </h3>
                      <p className="mt-1 text-[13.5px] text-[#64748B]">
                        People who are curious, collaborative, and excited about what's next.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col gap-3.5 pl-1 sm:pl-2">
                    {role.whatWereLookingFor.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-[#0284C7] mt-0.5" strokeWidth={2.2} />
                        <span className="text-[14px] leading-relaxed text-[#334155] font-medium">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card 3: What you'll work with */}
                <div
                  className="flex flex-col rounded-[16px] border bg-white p-7 sm:p-8"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    borderRadius: "16px",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#EBF8FE] text-[#0284C7] shadow-sm">
                      <Settings className="h-5 w-5" strokeWidth={2.2} />
                    </div>
                    <div>
                      <h3 className="text-[20px] font-bold text-[#0F172A] leading-snug">
                        What you'll work with
                      </h3>
                      <p className="mt-1 text-[13.5px] text-[#64748B]">
                        Modern tools and technologies to build what's next.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {role.techPills.map((pill) => {
                      const IconComponent = getRoleIcon(pill.icon);
                      return (
                        <div
                          key={pill.label}
                          className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2 text-[13px] font-semibold text-[#334155] shadow-xs hover:border-[#CBD5E1] transition-colors"
                        >
                          <IconComponent className="h-4 w-4 text-[#0284C7]" strokeWidth={2} />
                          <span>{pill.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Sidebar (4 cols) matching exact 4th screenshot specs */}
              <div className="flex flex-col gap-6 lg:col-span-4 lg:sticky lg:top-24">
                {/* Sidebar Card 1: Ready to build what's next? (Dark Card) */}
                <div
                  className="flex flex-col justify-between rounded-[16px] p-7 text-white"
                  style={{
                    backgroundColor: "rgba(11, 19, 32, 1)",
                    borderRadius: "16px",
                    minHeight: "213.9px",
                    boxShadow:
                      "0 4px 6px -4px rgba(0, 0, 0, 0.1), 0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div>
                    <h3 className="text-[19px] sm:text-[20px] font-bold leading-snug text-white">
                      Ready to build what's next?
                    </h3>
                    <p className="mt-2.5 text-[13.5px] leading-[21px] text-[#94A3B8]">
                      Join a team that's solving real problems with AI and creating meaningful impact.
                    </p>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={() => openModal("apply")}
                      className="flex h-[44px] w-full items-center justify-center gap-2 rounded-full bg-white text-[14px] font-bold text-[#0F172A] transition-all hover:bg-gray-100 hover:scale-[1.02] active:scale-98 shadow-md cursor-pointer"
                    >
                      Apply for this role <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Sidebar Card 2: Job details */}
                <div
                  className="flex flex-col rounded-[16px] border bg-white p-7"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    borderRadius: "16px",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <h4 className="text-[18px] font-bold text-[#0F172A]">
                    Job details
                  </h4>

                  <div className="mt-5 flex flex-col gap-4">
                    {/* Department */}
                    <div className="flex items-center gap-3.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F5F9] text-[#64748B]">
                        <Users className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11.5px] font-medium text-[#64748B]">
                          Department
                        </span>
                        <span className="text-[13.5px] font-bold text-[#0F172A]">
                          {role.dept}
                        </span>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-3.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F5F9] text-[#64748B]">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11.5px] font-medium text-[#64748B]">
                          Location
                        </span>
                        <span className="text-[13.5px] font-bold text-[#0F172A]">
                          {role.locationDisplay || role.loc}
                        </span>
                      </div>
                    </div>

                    {/* Experience */}
                    <div className="flex items-center gap-3.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F5F9] text-[#64748B]">
                        <Briefcase className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11.5px] font-medium text-[#64748B]">
                          Experience
                        </span>
                        <span className="text-[13.5px] font-bold text-[#0F172A]">
                          {role.experience}
                        </span>
                      </div>
                    </div>

                    {/* Work mode */}
                    <div className="flex items-center gap-3.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F5F9] text-[#64748B]">
                        <Home className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11.5px] font-medium text-[#64748B]">
                          Work mode
                        </span>
                        <span className="text-[13.5px] font-bold text-[#0F172A]">
                          {role.workMode}
                        </span>
                      </div>
                    </div>

                    {/* Role type */}
                    <div className="flex items-center gap-3.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F5F9] text-[#64748B]">
                        <Clock className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11.5px] font-medium text-[#64748B]">
                          Role type
                        </span>
                        <span className="text-[13.5px] font-bold text-[#0F172A]">
                          {role.employmentType || "Full-time"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Not an exact match Callout */}
                  <div className="mt-6 rounded-[14px] bg-[#F0F8FF] p-4 border border-[#BAE6FD]/40">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E0F2FE] text-[#0284C7]">
                        <Sparkles className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex flex-col">
                        <h5 className="text-[13px] font-bold text-[#0F172A]">
                          Not an exact match?
                        </h5>
                        <p className="mt-0.5 text-[12px] leading-[17px] text-[#64748B]">
                          We're always open to hearing from exceptional talent.
                        </p>
                        <button
                          onClick={() => openModal("introduce")}
                          className="mt-2 text-[12.5px] font-semibold text-[#007AFF] hover:underline text-left cursor-pointer"
                        >
                          Introduce Yourself →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Full Screen Application Modal matching Screenshot 1, 3, 4 */}
      <AnimatePresence>
        {isApplyModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 md:p-6 bg-black/65 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsApplyModalOpen(false)}
              className="fixed inset-0 cursor-pointer"
            />

            {/* Modal Card matching Screenshot 2 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 15 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 flex flex-col md:flex-row w-full max-w-[1240px] h-[92vh] max-h-[860px] overflow-hidden rounded-[24px] sm:rounded-[28px] bg-white shadow-[0_25px_70px_rgba(0,0,0,0.3)]"
              style={{
                border: "5px solid rgba(205, 205, 205, 1)",
              }}
            >
              {/* Left Column: Building Photo with frosted glass card matching Screenshot 2 */}
              <div className="relative hidden md:block w-[360px] lg:w-[400px] xl:w-[430px] shrink-0 h-full overflow-hidden bg-slate-900">
                <img
                  src="/images/careers/apply-modal-hero.png"
                  alt="Simpel AI Headquarters"
                  className="h-full w-full object-cover object-center"
                />

                {/* Bottom glassmorphic overlay card matching Screenshot 2 */}
                <div
                  className="absolute bottom-6 left-6 right-6 z-10 rounded-[20px] p-6 text-white shadow-2xl"
                  style={{
                    background: "rgba(255, 255, 255, 0.16)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
                  }}
                >
                  <h4 className="text-[20px] font-bold tracking-tight text-white">
                    Join the Future
                  </h4>
                  <p className="mt-2 text-[12.5px] sm:text-[13px] leading-[19px] text-white/90">
                    Experience a seamless transition into your new professional home. We're excited to have you on board.
                  </p>
                </div>
              </div>

              {/* Right Column: Scrollable Application Form */}
              <div className="relative flex-1 h-full overflow-y-auto p-6 sm:p-8 lg:p-10 bg-white">
                {/* Close Button matching Screenshot 2 */}
                <button
                  type="button"
                  onClick={() => setIsApplyModalOpen(false)}
                  className="absolute top-5 right-5 sm:top-6 sm:right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors cursor-pointer shadow-sm"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" strokeWidth={2} />
                </button>

                <form onSubmit={handleFormSubmit} className="clear-both flex flex-col pt-2 pr-8 sm:pr-10">
                  {/* Section 1: Personal Information */}
                  <div>
                    <h2 className="text-[26px] sm:text-[28px] font-bold text-[#0F172A] tracking-tight">
                      Personal Information
                    </h2>
                    <p className="mt-1 text-[13.5px] text-[#64748B]">
                      Tell us about yourself
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      {/* Full Name */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <User className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <input
                            type="text"
                            required
                            placeholder="Enter your full name"
                            value={formData.fullName}
                            onChange={(e) =>
                              setFormData({ ...formData, fullName: e.target.value })
                            }
                            className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                          />
                        </div>
                      </div>

                      {/* Email Address */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <input
                            type="email"
                            required
                            placeholder="you@example.com"
                            value={formData.email}
                            onChange={(e) =>
                              setFormData({ ...formData, email: e.target.value })
                            }
                            className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                          />
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <div className="flex h-[46px] items-center gap-1.5 rounded-[10px] border border-[#E2E8F0] bg-white px-3 text-[13px] font-semibold text-[#0F172A]">
                            <span>🇮🇳</span>
                            <span>+91</span>
                            <ChevronDown className="h-3.5 w-3.5 text-[#94A3B8]" />
                          </div>
                          <div className="relative flex-1">
                            <Smartphone className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                            <input
                              type="tel"
                              required
                              maxLength={10}
                              inputMode="numeric"
                              placeholder="10-digit mobile number"
                              value={formData.phone}
                              onChange={(e) => {
                                const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
                                setFormData({ ...formData, phone: digitsOnly });
                              }}
                              className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Current City */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Current City <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <MapPin className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <select
                            required
                            value={formData.city}
                            onChange={(e) =>
                              setFormData({ ...formData, city: e.target.value })
                            }
                            className="h-[46px] w-full appearance-none rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-9 text-[13.5px] text-[#0F172A] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] cursor-pointer"
                          >
                            <option value="">Select your city</option>
                            <option value="Bengaluru">Bengaluru</option>
                            <option value="Mumbai">Mumbai</option>
                            <option value="Delhi NCR">Delhi NCR</option>
                            <option value="Hyderabad">Hyderabad</option>
                            <option value="Pune">Pune</option>
                            <option value="Chennai">Chennai</option>
                            <option value="Kolkata">Kolkata</option>
                            <option value="Remote">Remote</option>
                            <option value="Other">Other</option>
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                        </div>
                      </div>

                      {/* LinkedIn Profile */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          LinkedIn Profile
                        </label>
                        <div className="relative">
                          <Linkedin className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <input
                            type="url"
                            placeholder="https://www.linkedin.com/in/yourprofile"
                            value={formData.linkedin}
                            onChange={(e) =>
                              setFormData({ ...formData, linkedin: e.target.value })
                            }
                            className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                          />
                        </div>
                      </div>

                      {/* GitHub / Portfolio */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          GitHub / Portfolio{" "}
                          <span className="text-[#94A3B8] font-normal">
                            (Optional)
                          </span>
                        </label>
                        <div className="relative">
                          <GitFork className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <input
                            type="url"
                            placeholder="https://github.com/yourusername"
                            value={formData.portfolio}
                            onChange={(e) =>
                              setFormData({ ...formData, portfolio: e.target.value })
                            }
                            className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                          />
                        </div>
                      </div>

                      {/* Upload Resume / CV */}
                      <div className="sm:col-span-2 mt-1">
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Upload Resume / CV <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-[14px] border border-[#DCEBF7] bg-[#F4F9FD] p-4 sm:p-5 transition-colors">
                          <div className="flex items-center gap-3.5">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white border border-[#D5E7F5] text-[#0091FF] shadow-xs">
                              <Upload className="h-5 w-5" strokeWidth={2.2} />
                            </div>
                            <div>
                              <p className="text-[13.5px] font-bold text-[#0F172A]">
                                {resumeFile
                                  ? resumeFile.name
                                  : "Drag and drop your resume here"}
                              </p>
                              <p className="text-[12px] text-[#64748B] mt-0.5">
                                {resumeFile
                                  ? `${(resumeFile.size / (1024 * 1024)).toFixed(
                                      2
                                    )} MB • Click Choose File to change`
                                  : "PDF, DOC or DOCX (Max 5 MB)"}
                              </p>
                            </div>
                          </div>
                          <label className="shrink-0 cursor-pointer rounded-[10px] bg-gradient-to-r from-[#00C2E8] to-[#1E88E5] hover:opacity-95 px-6 py-2.5 text-[13.5px] font-semibold text-white shadow-sm transition-all cursor-pointer">
                            Choose File
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={handleResumeChange}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Professional Information */}
                  <div className="mt-8 pt-4 border-t border-[#F1F5F9]">
                    <h2 className="text-[26px] sm:text-[28px] font-bold text-[#0F172A] tracking-tight">
                      Professional Information
                    </h2>
                    <p className="mt-1 text-[13.5px] text-[#64748B]">
                      Tell us about yourself
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      {/* Current Employment Status */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Current Employment Status{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Briefcase className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <select
                            required
                            value={formData.employmentStatus}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                employmentStatus: e.target.value,
                              })
                            }
                            className="h-[46px] w-full appearance-none rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-9 text-[13.5px] text-[#0F172A] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] cursor-pointer"
                          >
                            <option value="">Select employment status</option>
                            <option value="Employed">Employed</option>
                            <option value="Notice Period">
                              Serving Notice Period
                            </option>
                            <option value="Actively Looking">
                              Actively Looking / Immediate
                            </option>
                            <option value="Student">Student / Fresher</option>
                            <option value="Freelancer">
                              Freelancer / Self-Employed
                            </option>
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                        </div>
                      </div>

                      {/* Years of Experience */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Years of Experience{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <BarChart2 className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <select
                            required
                            value={formData.experience}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                experience: e.target.value,
                              })
                            }
                            className="h-[46px] w-full appearance-none rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-9 text-[13.5px] text-[#0F172A] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] cursor-pointer"
                          >
                            <option value="">Select years of experience</option>
                            <option value="0-1">0 - 1 years</option>
                            <option value="1-3">1 - 3 years</option>
                            <option value="3-5">3 - 5 years</option>
                            <option value="5-8">5 - 8 years</option>
                            <option value="8+">8+ years</option>
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                        </div>
                      </div>

                      {/* Current Company (Optional) */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Current Company{" "}
                          <span className="text-[#94A3B8] font-normal">
                            (Optional)
                          </span>
                        </label>
                        <div className="relative">
                          <Building2 className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <input
                            type="text"
                            placeholder="Enter current company name"
                            value={formData.company}
                            onChange={(e) =>
                              setFormData({ ...formData, company: e.target.value })
                            }
                            className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                          />
                        </div>
                      </div>

                      {/* Current Role (Optional) */}
                      <div>
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Current Role{" "}
                          <span className="text-[#94A3B8] font-normal">
                            (Optional)
                          </span>
                        </label>
                        <div className="relative">
                          <UserCheck className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <input
                            type="text"
                            placeholder="Enter current role / designation"
                            value={formData.currentRole}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                currentRole: e.target.value,
                              })
                            }
                            className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                          />
                        </div>
                      </div>

                      {/* 3 Columns Row: Notice Period, Current CTC, Expected CTC */}
                      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Notice Period */}
                        <div>
                          <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                            Notice Period <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Clock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                            <select
                              required
                              value={formData.noticePeriod}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  noticePeriod: e.target.value,
                                })
                              }
                              className="h-[46px] w-full appearance-none rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-9 text-[13.5px] text-[#0F172A] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] cursor-pointer"
                            >
                              <option value="">Select notice period</option>
                              <option value="Immediate">Immediate</option>
                              <option value="15 Days">15 Days</option>
                              <option value="30 Days">30 Days</option>
                              <option value="60 Days">60 Days</option>
                              <option value="90 Days">90 Days</option>
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          </div>
                        </div>

                        {/* Current CTC */}
                        <div>
                          <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                            Current CTC (INR){" "}
                            <span className="text-[#94A3B8] font-normal">
                              (Optional)
                            </span>
                          </label>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] font-semibold text-[#64748B]">
                              ₹
                            </span>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="e.g. 12,00,000"
                              value={formData.currentCtc}
                              onChange={(e) => {
                                const numericVal = e.target.value.replace(/[^\d,]/g, "");
                                setFormData({
                                  ...formData,
                                  currentCtc: numericVal,
                                });
                              }}
                              className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-9 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                            />
                          </div>
                        </div>

                        {/* Expected CTC */}
                        <div>
                          <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                            Expected CTC (INR){" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] font-semibold text-[#64748B]">
                              ₹
                            </span>
                            <input
                              type="text"
                              required
                              inputMode="numeric"
                              placeholder="e.g. 18,00,000"
                              value={formData.expectedCtc}
                              onChange={(e) => {
                                const numericVal = e.target.value.replace(/[^\d,]/g, "");
                                setFormData({
                                  ...formData,
                                  expectedCtc: numericVal,
                                });
                              }}
                              className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-9 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Key Skills */}
                      <div className="sm:col-span-2">
                        <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5">
                          Key Skills <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Code2 className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <input
                            type="text"
                            required
                            placeholder="e.g. Python, ROS2, Computer Vision, Machine Learning"
                            value={formData.keySkills}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                keySkills: e.target.value,
                              })
                            }
                            className="h-[46px] w-full rounded-[10px] border border-[#E2E8F0] bg-white pl-10 pr-3 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]/20"
                          />
                        </div>
                        <p className="mt-1 text-[11.5px] text-[#94A3B8]">
                          Add skills separated by commas
                        </p>
                      </div>

                      {/* Submit Button matching Screenshot 3 */}
                      <div className="sm:col-span-2 mt-4 flex justify-end">
                        <button
                          type="submit"
                          className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-[14.5px] font-semibold text-white transition-all hover:scale-105 active:scale-95 shadow-md hover:shadow-lg cursor-pointer"
                          style={{
                            background:
                              "linear-gradient(90deg, #00D2EE 0%, #007AFF 100%)",
                          }}
                        >
                          Submit your application <ArrowRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AiEngineerRole;
